import { useEffect, useMemo, useState } from "react";
import { listBookings, deleteBooking, listContacts, getStats } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Trash2, Phone, Download, RefreshCw } from "lucide-react";
import { whatsappLink } from "@/lib/whatsapp";

export default function Admin() {
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ bookings: 0, contacts: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, s] = await Promise.all([listBookings(), listContacts(), getStats()]);
      setBookings(b);
      setContacts(c);
      setStats(s);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return bookings;
    const s = q.toLowerCase();
    return bookings.filter((b) =>
      [b.name, b.mobile, b.pickup_location, b.drop_location, b.vehicle_type].some((v) => (v || "").toLowerCase().includes(s))
    );
  }, [bookings, q]);

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await deleteBooking(id);
      toast.success("Booking deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const exportCsv = () => {
    const rows = [["name","mobile","pickup","drop","date","time","vehicle","message","created_at"]];
    filtered.forEach((b) => rows.push([b.name,b.mobile,b.pickup_location,b.drop_location,b.pickup_date,b.pickup_time,b.vehicle_type,(b.message||"").replace(/[\r\n,]/g," "),b.created_at]));
    const csv = rows.map((r) => r.map((c) => `"${(c||"").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mamta-bookings-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-page">
      <header className="bg-white border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="admin-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="admin-export"><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-blue">Admin Console</div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-black">Bookings & Enquiries</h1>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Total Bookings" value={stats.bookings} data-testid="stat-bookings" />
            <Stat label="Total Contacts" value={stats.contacts} />
          </div>
        </div>

        <Tabs defaultValue="bookings" className="mt-10">
          <TabsList className="bg-white border border-black/5">
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({contacts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <div className="mt-6 bg-white rounded-2xl border border-black/5 shadow-[0_2px_20px_rgba(11,77,184,0.04)]">
              <div className="p-4 border-b border-black/5 flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, mobile, city…" className="pl-9" data-testid="admin-search" />
                </div>
                {loading && <div className="text-xs text-brand-ink/50">Loading…</div>}
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Date / Time</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((b) => (
                      <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                        <TableCell className="font-semibold">{b.name}</TableCell>
                        <TableCell>{b.mobile}</TableCell>
                        <TableCell><span className="text-brand-blue">{b.pickup_location}</span> → <span className="text-brand-orange">{b.drop_location || "—"}</span></TableCell>
                        <TableCell className="text-xs">{b.pickup_date || "—"} <br/> <span className="text-brand-ink/60">{b.pickup_time || ""}</span></TableCell>
                        <TableCell><Badge variant="outline" className="border-brand-blue/30 text-brand-blue">{b.vehicle_type || "—"}</Badge></TableCell>
                        <TableCell className="text-xs text-brand-ink/60">{new Date(b.created_at).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1">
                            <a href={`tel:${b.mobile}`} className="w-8 h-8 rounded-md bg-brand-blue text-white flex items-center justify-center hover:bg-brand-orange transition-colors" aria-label="Call"><Phone className="w-3.5 h-3.5" /></a>
                            <a href={whatsappLink(`Hi ${b.name}, this is Mamta Tours regarding your booking.`)} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-[#25D366] text-white flex items-center justify-center" aria-label="WhatsApp"><WhatsAppIcon size={14} /></a>
                            <button onClick={() => remove(b.id)} className="w-8 h-8 rounded-md bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center" aria-label="Delete" data-testid={`delete-booking-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filtered.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-brand-ink/50 py-12">No bookings yet.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="mt-6 bg-white rounded-2xl border border-black/5 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-semibold">{c.name}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.phone || "—"}</TableCell>
                      <TableCell className="max-w-md truncate">{c.message}</TableCell>
                      <TableCell className="text-xs text-brand-ink/60">{new Date(c.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {contacts.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-brand-ink/50 py-12">No contact enquiries yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, ...rest }) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 px-6 py-4 min-w-[160px]" {...rest}>
      <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-brand-ink/50">{label}</div>
      <div className="font-display text-3xl font-black text-brand-blue mt-1">{value}</div>
    </div>
  );
}
